import { intlFormat, parseISO, setDay } from 'date-fns';
import { AlignLeftIcon, MapPinIcon, StoreIcon, UsersIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/card.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { CrmEventDetails } from '../../../server/types/infer-trpc-types.ts';
import { parseRecurrenceString } from '../../../utils/rrule.ts';
import { formatISOTimeForDisplay } from '../../../utils/time-utils.ts';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';

type EventViewProps = {
    eventDetails: CrmEventDetails;
    occurrenceDate?: string;
};

const getGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

export const EventDetailsSchedule = ({ eventDetails, occurrenceDate }: EventViewProps) => {
    const { t, locale } = useLocale('events');
    const navigate = useNavigate();

    const headerDate = occurrenceDate ?? eventDetails.date;
    const dateFormatted = headerDate
        ? intlFormat(
              parseISO(headerDate),
              {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
              },
              { locale },
          )
        : '';

    const timeFormatted =
        eventDetails.start_time && eventDetails.end_time
            ? t('card_time_format', {
                  startTime: formatISOTimeForDisplay(eventDetails.start_time),
                  endTime: formatISOTimeForDisplay(eventDetails.end_time),
              })
            : eventDetails.start_time
              ? formatISOTimeForDisplay(eventDetails.start_time)
              : '';

    function getRecurrenceText() {
        if (!eventDetails.recurrence) {
            return undefined;
        }

        const recurrence = parseRecurrenceString(eventDetails.recurrence, eventDetails.startDate);

        if (!recurrence) {
            return;
        }

        const { interval, weekdays, until } = recurrence;

        if (!interval || !weekdays?.length) {
            return;
        }

        return (
            <>
                <Text color="neutral-950" size="sm">
                    {t('events__schedule__recurrence', {
                        count: interval,
                        weekdays: weekdays
                            .toSorted()
                            .map((weekday) =>
                                new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(
                                    setDay(new Date(), weekday),
                                ),
                            )
                            .join(', '),
                    })}
                </Text>
                {until && (
                    <Text color="neutral-950" size="sm">
                        {t('events__schedule__from_until', {
                            from: intlFormat(
                                parseISO(eventDetails.startDate),
                                { day: '2-digit', month: 'long', year: 'numeric' },
                                { locale },
                            ),
                            until: intlFormat(
                                parseISO(until),
                                { day: '2-digit', month: 'long', year: 'numeric' },
                                { locale },
                            ),
                        })}
                    </Text>
                )}
            </>
        );
    }

    return (
        <>
            <Card>
                <div className="flex flex-col gap-3">
                    <Text weight="semibold" color="neutral-950" size="base">
                        {eventDetails.title ?? t('description_empty')}
                    </Text>

                    <div className="flex flex-col gap-2">
                        <div className="flex flex-col sm:flex-row">
                            <Text color="neutral-950" size="sm">
                                {dateFormatted}
                            </Text>
                            {!!timeFormatted && (
                                <>
                                    <Text color="neutral-950" size="sm" className="hidden sm:inline">
                                        &nbsp;•&nbsp;
                                    </Text>
                                    <Text color="neutral-950" size="sm">
                                        {timeFormatted}
                                    </Text>
                                </>
                            )}
                        </div>

                        {getRecurrenceText()}
                    </div>
                </div>

                <div className="flex flex-col gap-3">
                    {/* Customer */}
                    {eventDetails.customer && (
                        <div className="flex items-start gap-2">
                            <StoreIcon className="mt-px shrink-0 text-neutral-600" />
                            <div className="flex flex-col gap-1">
                                <Text color="neutral-600" size="sm" weight="medium">
                                    {t('events__customer_label')}
                                </Text>
                                <LinkText
                                    href={`/customers/${eventDetails.customer.customerId}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        void navigate(`/customers/${eventDetails.customerId}`);
                                    }}
                                >
                                    {eventDetails.customer.customerName}
                                </LinkText>
                            </div>
                        </div>
                    )}

                    {/* Location */}
                    {eventDetails.address && (
                        <div className="flex items-start gap-2">
                            <MapPinIcon className="mt-px shrink-0 text-neutral-600" />
                            <div className="flex flex-col gap-1">
                                <Text color="neutral-600" weight="medium">
                                    {t('events__location_label')}
                                </Text>
                                <LinkText
                                    href={getGoogleMapsUrl(eventDetails.address)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {eventDetails.address}
                                </LinkText>
                            </div>
                        </div>
                    )}

                    {/* Attendees */}
                    {(eventDetails.owner_email ?? eventDetails.attendees.length > 0) && (
                        <div className="flex items-start gap-2">
                            <UsersIcon className="mt-px shrink-0 text-neutral-600" />
                            <div className="flex flex-col gap-1">
                                <Text color="neutral-600" size="sm" weight="medium">
                                    {t('events__attendees_label')}
                                </Text>
                                <div className="flex flex-col gap-0.5">
                                    {eventDetails.owner_email && (
                                        <div className="flex items-center gap-1.5">
                                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-950" />
                                            <Text size="sm" color="neutral-950">
                                                {eventDetails.owner_email}{' '}
                                                <Text as="span" size="sm" color="neutral-500">
                                                    ({t('events__attendees_owner')})
                                                </Text>
                                            </Text>
                                        </div>
                                    )}
                                    {eventDetails.attendees.map((email) => (
                                        <div key={email} className="flex items-center gap-1.5">
                                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-950" />
                                            <Text size="sm" color="neutral-950">
                                                {email}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Card>

            {eventDetails.agendaRich && (
                <Card>
                    <div className="flex items-start gap-2">
                        <AlignLeftIcon className="mt-px shrink-0 text-neutral-600" />
                        <div className="flex flex-col gap-1">
                            <Text color="neutral-600" weight="medium">
                                {t('events__agenda_label')}
                            </Text>
                            <div className="text-sm text-neutral-950">
                                <RichTextViewer content={eventDetails.agendaRich} />
                            </div>
                        </div>
                    </div>
                </Card>
            )}
        </>
    );
};
